import { ChangeEvent, FormEvent, useEffect } from 'react';
import { useForm, UseFormReturn } from 'react-hook-form';

import { useTranslation } from '@gem/modules/i18n';
import { patterns } from '@gem/utils';

import { IDiscoverDestination } from 'APP/model/tenantRouter/tenantRouterModel.types';
import { storage } from 'APP/packages/storage';
import { ProjectInstallationType } from 'CONFIG_TYPES';
import Entities from 'STORE';
import { createDiscoverDestinationWithKey } from 'UTILS/discoverDestinationsUtils';

import { ILoginViewProps } from '../../Login.types';

export interface ILoginAddNewServerStepFormData {
  baseUrl: string;
  alias: string;
}

export interface ILoginAddNewServerStepProps extends ILoginViewProps {}
export interface ILoginAddNewServerStepPresenterParams extends ILoginAddNewServerStepProps {}

interface ILoginAddNewServerStepPresenter {
  onBaseUrlChange(e: ChangeEvent<HTMLInputElement>): void;
  submitNewServerDestination(data: ILoginAddNewServerStepFormData): Promise<void>;
  onSubmit(e: FormEvent<HTMLFormElement>): void;
  normalizeUrl(): void;
  onBackHandler: (() => void) | null;
}

interface IUseLoginAddNewServerStepPresenter {
  presenter: ILoginAddNewServerStepPresenter;
  form: UseFormReturn<ILoginAddNewServerStepFormData>;
}

export const BASE_URL_PROTOCOL_STRING = 'https://';
export const BASE_URL_EXAMPLE = 'server.company.com';

function getCleanBaseUrl(baseUrl: string): string {
  return baseUrl.replace(patterns.compile(patterns.PROTOCOL), '').trim();
}

export function useLoginAddNewServerStepPresenter(
  params: ILoginAddNewServerStepPresenterParams
): IUseLoginAddNewServerStepPresenter {
  const { loginPresenter } = params;
  const { t } = useTranslation();
  const form = useForm<ILoginAddNewServerStepFormData>({
    mode: 'onTouched',
    defaultValues: {
      baseUrl: getCleanBaseUrl(loginPresenter.destination?.baseUrl || ''),
      alias: loginPresenter.destination?.alias || '',
    },
  });

  const presenter: ILoginAddNewServerStepPresenter = {
    onBaseUrlChange(e: ChangeEvent<HTMLInputElement>): void {
      const baseUrl = e.target.value;
      form.setValue('baseUrl', getCleanBaseUrl(baseUrl), {
        shouldValidate: true,
      });
    },

    async submitNewServerDestination(data: ILoginAddNewServerStepFormData): Promise<void> {
      const destination: IDiscoverDestination = createDiscoverDestinationWithKey({
        alias: data.alias,
        baseUrl: `${BASE_URL_PROTOCOL_STRING}${data.baseUrl}`,
        type: ProjectInstallationType.OnPremiseOnly,
        isLocal: true,
      });

      Entities.authStore.addDiscoverDestinations([destination]);
      const canAuthorize = await loginPresenter.onAuthorizeWithNewConfig(destination.key);

      if (!canAuthorize) {
        Entities.authStore.removeDiscoverDestination(destination.key);
        return;
      }

      storage.discoverDestinations.add(loginPresenter.email, destination);
    },

    onSubmit(e: FormEvent<HTMLFormElement>): void {
      presenter.normalizeUrl();
      form.handleSubmit(presenter.submitNewServerDestination)(e);
    },

    normalizeUrl(): void {
      try {
        const baseUrl = new URL(`${BASE_URL_PROTOCOL_STRING}${form.getValues('baseUrl')}`);
        const cleanPathname = baseUrl.pathname.replace(/\/+\s*$/, '');
        form.setValue('baseUrl', `${baseUrl.host}${cleanPathname}`);
      } catch {
        form.setError('baseUrl', { message: t('common_invalid_url') });
      }
    },

    get onBackHandler(): (() => void) | null {
      if (!loginPresenter.onBackHandler) {
        return null;
      }

      return () => {
        loginPresenter.setDestinationKey('');
        loginPresenter.onBackHandler?.();
      };
    },
  };

  useEffect(() => {
    if (loginPresenter.isRouterError) {
      form.setError('baseUrl', {});
    }
  }, [loginPresenter.isRouterError]);

  return { presenter, form };
}
